import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Coins, Sparkle, Trophy, ChartLineUp } from "@phosphor-icons/react";

export default function Profile() {
  const { user_id } = useParams();
  const [user, setUser] = useState(null);
  const [rep, setRep] = useState(null);
  const [badges, setBadges] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [dna, setDna] = useState(null);

  useEffect(() => {
    (async () => {
      const [users, r, c, cap] = await Promise.all([
        api.get("/users?limit=1000"), api.get(`/reputation/${user_id}`), api.get("/badges/catalog"),
        api.get(`/capability/profile/${user_id}`).catch(() => ({ data: null })),
      ]);
      setUser(users.data.find((u) => u.user_id === user_id));
      setRep(r.data);
      setCatalog(c.data);
      setDna(cap.data);
    })();
  }, [user_id]);

  if (!user) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  const initials = (user.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto" data-testid="profile-page">
      <div className="flex items-start gap-5 mb-8">
        <Avatar className="h-20 w-20">
          <AvatarImage src={user.avatar} />
          <AvatarFallback className="text-lg">{initials}</AvatarFallback>
        </Avatar>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{user.role}</div>
          <h1 className="text-3xl tracking-tight font-bold mt-1" data-testid="profile-name">{user.name}</h1>
          <p className="text-sm text-zinc-500">{user.email}</p>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(user.skills || []).map((s) => (
              <Badge key={s} variant="outline" className="rounded-sm font-mono text-[10px] uppercase tracking-wider">{s}</Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
        <div className="border border-border p-4 bg-background" data-testid="profile-credits">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1"><Coins size={11} /> Credits</div>
          <div className="text-2xl mt-2 font-bold font-mono text-[#E1252A]">{rep?.balance ?? 0}</div>
        </div>
        <div className="border border-border p-4 bg-background" data-testid="profile-impact">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1"><Sparkle size={11} /> Impact</div>
          <div className="text-2xl mt-2 font-bold font-mono">{rep?.impact_points ?? 0}</div>
        </div>
        <div className="border border-border p-4 bg-background" data-testid="profile-completed">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1"><Trophy size={11} /> Completed</div>
          <div className="text-2xl mt-2 font-bold font-mono">{rep?.completed_tasks ?? 0}</div>
        </div>
        <div className="border border-border p-4 bg-background" data-testid="profile-month">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1"><ChartLineUp size={11} /> This month</div>
          <div className="text-2xl mt-2 font-bold font-mono">{rep?.monthly_earned ?? 0}</div>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold tracking-tight mb-3">Recent reputation events</h2>        <div className="border border-border bg-background">
          <table className="w-full text-sm">
            <thead className="border-b border-border"><tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <th className="text-left py-2 px-3">When</th>
              <th className="text-left py-2 px-3">Reason</th>
              <th className="text-right py-2 px-3">Pts</th>
              <th className="text-right py-2 px-3">Impact</th>
            </tr></thead>
            <tbody>
              {(rep?.events || []).map((e) => (
                <tr key={e.event_id} className="border-b border-border">
                  <td className="py-2 px-3 text-zinc-500 text-xs">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="py-2 px-3">{e.reason}</td>
                  <td className="py-2 px-3 text-right font-mono">{e.points}</td>
                  <td className="py-2 px-3 text-right font-mono text-[#E1252A]">{e.impact_points}</td>
                </tr>
              ))}
              {(rep?.events || []).length === 0 && (
                <tr><td colSpan={4} className="text-center py-12 text-sm text-zinc-500">No reputation events yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
